/**
 * O painel direito: a cena aberta, inteira.
 *
 * <p><b>Não há botão "Salvar".</b> Como em toda ficha da Criação do Mundo, o campo grava
 * ao perder o foco e o vínculo grava ao ser escolhido. O protótipo desta aba tinha um
 * botão, mas adotá-lo faria esta ser a única tela do módulo onde digitar e clicar fora
 * perde o que foi escrito — e a mão do time já está treinada no contrário.
 *
 * <p>Todo campo de vínculo aqui é <b>somente leitura sobre o mundo</b>: escolher um NPC
 * põe o id dele nesta cena e não encosta na ficha do NPC. É por isso que o seletor vem
 * com `permitirCriar` desligado — o atalho "criar" dele cadastraria uma ficha de verdade
 * na coleção alvo, que é justamente o que esta aba não pode fazer.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useImagemAutenticada } from '../../../comum/ImagemAutenticada';
import * as mundoApi from '../mundoApi';
import { SeletorDeRelacao } from '../SeletorDeRelacao';
import type { Colecao } from '../tipos';
import type { Cena, OpcaoDaCena, ResumoDeCena, TipoDeCena, VinculoDoMundo } from './cenasTipos';
import {
  ALVO_DO_TIPO,
  COR_DO_TIPO,
  NOME_DO_ALVO,
  ROTULO_DO_TIPO,
  TIPOS_DE_CENA,
} from './cenasTipos';

interface Props {
  cena: Cena;
  /** Destinos possíveis para uma escolha: todas menos ela mesma. */
  destinosPossiveis: ResumoDeCena[];
  aoAplicar: (mudancas: Record<string, unknown>) => void;
  aoNovoPrologo: () => void;
  aoExcluir: () => void;
  salvando: boolean;
}

export function EditorDeCena({
  cena,
  destinosPossiveis,
  aoAplicar,
  aoNovoPrologo,
  aoExcluir,
  salvando,
}: Props) {
  const navegar = useNavigate();

  // As escolhas precisam de estado local porque uma linha recém-criada ainda não tem
  // texto, e uma opção sem texto não vai para o banco. Os demais campos gravam direto.
  const [opcoes, setOpcoes] = useState<OpcaoDaCena[]>(() => cena.opcoes.map((o) => ({ ...o })));
  const [enviandoImagem, setEnviandoImagem] = useState(false);

  useEffect(() => {
    setOpcoes(cena.opcoes.map((o) => ({ ...o })));
  }, [cena]);

  const colecaoDoAlvo = ALVO_DO_TIPO[cena.tipo];
  // A wiki é interna: o servidor exige sessão até para ler imagem, e `background-image`
  // não manda o token. O hook baixa por fetch autenticado e devolve um object URL.
  const capa = useImagemAutenticada(cena.imagem);

  /** Manda a lista inteira, sem as linhas que ficaram sem texto. */
  function gravarOpcoes(lista: OpcaoDaCena[]) {
    aoAplicar({
      opcoes: lista
        .filter((o) => o.texto.trim())
        .map((o) => ({ texto: o.texto.trim(), destinoId: o.destino?.id ?? null })),
    });
  }

  async function enviarImagem(arquivo?: File) {
    if (!arquivo) return;
    setEnviandoImagem(true);
    try {
      const salvo = await mundoApi.enviarImagem(arquivo);
      aoAplicar({ imagem: salvo.url });
    } finally {
      setEnviandoImagem(false);
    }
  }

  /** Um seletor de ficha do mundo: escolhe entre o que já existe, e só isso. */
  function Vinculo({
    rotulo,
    colecao,
    valor,
    chave,
    dica,
  }: {
    rotulo: string;
    colecao: Colecao;
    valor?: VinculoDoMundo | null;
    /** O par de campos do PATCH: `${chave}Id` para apontar, `remover…` para soltar. */
    chave: 'area' | 'local' | 'alvo' | 'quest';
    dica?: string;
  }) {
    const remover = `remover${chave[0].toUpperCase()}${chave.slice(1)}`;
    return (
      <div className="cn-campo">
        <label>
          {rotulo} <span className="cn-ro">do sistema</span>
        </label>
        <SeletorDeRelacao
          alvo={colecao}
          unico
          permitirCriar={false}
          selecionados={valor ? [valor] : []}
          aoAbrir={(c, id) => navegar(`/mundo/${c}/${id}`)}
          aoMudar={(ids) =>
            aoAplicar(ids.length ? { [`${chave}Id`]: ids[0] } : { [remover]: true })
          }
        />
        {dica && <div className="cn-dica">{dica}</div>}
      </div>
    );
  }

  return (
    <div className="cn-editor">
      <div className="cn-ed-topo">
        <span
          className="cn-badge"
          style={{ '--c': COR_DO_TIPO[cena.tipo] } as React.CSSProperties}
        >
          {ROTULO_DO_TIPO[cena.tipo]}
        </span>
        <span className="cn-caminho">
          {cena.area ? cena.area.nome : 'sem área'}
          {cena.local ? ` › ${cena.local.nome}` : ''}
        </span>
        {salvando && <span className="cn-salvando">salvando…</span>}
      </div>

      <div className="cn-nota">
        A hierarquia vem <b>do mundo</b>: o NPC aparece dentro do local onde mora, e o local
        dentro da área dele. Mudar isto se faz na ficha, e não aqui. O <b>código</b> é só
        rótulo de leitura. Os campos marcados <span className="cn-ro">do sistema</span> leem o
        conteúdo já cadastrado e não o alteram.
      </div>

      <div className="cn-tres">
        <div className="cn-campo">
          <label>Nome</label>
          <input
            defaultValue={cena.nome}
            onBlur={(e) => {
              const novo = e.target.value.trim();
              if (novo && novo !== cena.nome) aoAplicar({ nome: novo });
            }}
          />
        </div>
        <div className="cn-campo">
          <label>Tipo</label>
          {/* Grava na hora, e não junto com o resto: é o tipo que decide para qual
              ficha a cena aponta, e o servidor precisa saber o tipo novo antes de
              receber o vínculo novo. */}
          <select
            value={cena.tipo}
            onChange={(e) => aoAplicar({ tipo: e.target.value as TipoDeCena })}
          >
            {TIPOS_DE_CENA.map((t) => (
              <option key={t} value={t}>
                {ROTULO_DO_TIPO[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="cn-campo">
          <label>Código</label>
          <input
            defaultValue={cena.codigo ?? ''}
            placeholder="ex.: 111201"
            onBlur={(e) => {
              if (e.target.value !== (cena.codigo ?? '')) aoAplicar({ codigo: e.target.value });
            }}
          />
        </div>
      </div>

      {/*
        Não há seletor de "cena mãe", e não há mais coluna `pai_id` por trás dele: a
        V25 a apagou. A hierarquia vem da geografia — o NPC está sob o local onde mora,
        o local está sob a área —, e um seletor que não move nada na árvore seria pior
        do que nenhum, porque quem o usasse acharia que moveu.
      */}

      <div className="cn-dois">
        <Vinculo rotulo="Área" colecao="areas" valor={cena.area} chave="area" />
        <Vinculo rotulo="Local" colecao="locais" valor={cena.local} chave="local" />
      </div>

      {colecaoDoAlvo && (
        <Vinculo
          rotulo={`${NOME_DO_ALVO[cena.tipo]} exibido`}
          colecao={colecaoDoAlvo}
          valor={cena.alvo}
          chave="alvo"
          dica={`Cena do tipo ${ROTULO_DO_TIPO[cena.tipo]} aponta para ${
            NOME_DO_ALVO[cena.tipo]
          } já cadastrado.`}
        />
      )}

      <Vinculo
        rotulo="Quest"
        colecao="quests"
        valor={cena.quest}
        chave="quest"
        dica="Opcional, e independente do tipo: qualquer cena pode pertencer a uma quest."
      />

      <div className="cn-campo">
        <label>Narrativa</label>
        <textarea
          defaultValue={cena.narrativa ?? ''}
          rows={5}
          onBlur={(e) => {
            if (e.target.value !== (cena.narrativa ?? '')) {
              aoAplicar({ narrativa: e.target.value });
            }
          }}
        />
      </div>

      <div className="cn-campo">
        <label>Imagem</label>
        <label
          className="cn-imagem"
          style={capa ? { backgroundImage: `url('${capa}')` } : undefined}
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => void enviarImagem(e.target.files?.[0])}
          />
          {!cena.imagem && !enviandoImagem && <span>clique para enviar</span>}
          {enviandoImagem && <span>enviando…</span>}
        </label>
        {cena.imagem && (
          <button type="button" className="cn-tirar" onClick={() => aoAplicar({ imagem: '' })}>
            remover imagem
          </button>
        )}
      </div>

      <div className="cn-campo">
        <label>Descrição da imagem</label>
        <textarea
          defaultValue={cena.descImagem ?? ''}
          rows={2}
          onBlur={(e) => {
            if (e.target.value !== (cena.descImagem ?? '')) {
              aoAplicar({ descImagem: e.target.value });
            }
          }}
        />
      </div>

      <div className="cn-campo">
        <label>Opções (escolha → cena destino)</label>
        <div className="cn-opcoes">
          {opcoes.map((opcao, i) => (
            <div className="cn-opcao" key={opcao.id ?? `nova-${i}`}>
              <input
                value={opcao.texto}
                placeholder='ex.: "Abrir a porta"'
                onChange={(e) => {
                  const novas = [...opcoes];
                  novas[i] = { ...opcao, texto: e.target.value };
                  setOpcoes(novas);
                }}
                onBlur={() => gravarOpcoes(opcoes)}
              />
              <select
                value={opcao.destino?.id ?? ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  const novas = [...opcoes];
                  novas[i] = {
                    ...opcao,
                    destino: id ? (destinosPossiveis.find((d) => d.id === id) ?? null) : null,
                  };
                  setOpcoes(novas);
                  gravarOpcoes(novas);
                }}
              >
                <option value="">— destino —</option>
                {destinosPossiveis.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="cn-x"
                title="Remover escolha"
                onClick={() => {
                  const novas = opcoes.filter((_, j) => j !== i);
                  setOpcoes(novas);
                  gravarOpcoes(novas);
                }}
              >
                ×
              </button>
            </div>
          ))}
          {opcoes.length === 0 && (
            <span className="cn-dica">Nenhuma escolha. Cena final ou de passagem.</span>
          )}
        </div>
        <button
          type="button"
          className="cn-mais"
          onClick={() => setOpcoes([...opcoes, { texto: '', destino: null }])}
        >
          + opção
        </button>
      </div>

      <div className="cn-campo">
        <label>Recompensas</label>
        <textarea
          defaultValue={cena.recompensas ?? ''}
          rows={2}
          onBlur={(e) => {
            if (e.target.value !== (cena.recompensas ?? '')) {
              aoAplicar({ recompensas: e.target.value });
            }
          }}
        />
      </div>

      <div className="cn-acoes">
        {/* A única cena que não nasce de um nó da árvore: o prólogo, que acontece
            antes de o jogador estar em lugar nenhum. Todo o resto se cria abrindo o
            local, o NPC ou a criatura que já existe no mundo. */}
        <button
          type="button"
          className="cn-btn"
          title="Cria uma cena fora do mapa, em 'Sem área · prólogo'."
          onClick={aoNovoPrologo}
        >
          + Cena de prólogo
        </button>
        <button type="button" className="cn-btn perigo" onClick={aoExcluir}>
          Excluir
        </button>
      </div>

      {cena.autoria.atualizadoPor && (
        <div className="cn-autoria">Última edição por {cena.autoria.atualizadoPor}</div>
      )}
    </div>
  );
}
